/**
 * FunnelHealthCard — MA-FUNNEL-HEALTH-SCORE-013 + MA-FUNNEL-AUTO-FIX-014
 * 
 * Detailed health assessment card for the Funnel Builder page.
 * Shows score, dimension breakdown, clickable issue list with auto-fix buttons.
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  Shield, ShieldCheck, ShieldAlert, ShieldX,
  ChevronDown, ChevronUp, AlertTriangle, Info, XCircle,
  GitBranch, Target, Zap, FileText, Workflow,
  Wrench, Loader2, Check, Eye,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { FixDiffPanel } from './FixDiffPanel';

const STATUS_CONFIG = {
  healthy:   { icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', label: 'Healthy — Ready to publish', barColor: 'bg-emerald-500' },
  good:      { icon: Shield,      color: 'text-sky-400',     bg: 'bg-sky-500/10',     border: 'border-sky-500/20',     label: 'Good — Minor issues',       barColor: 'bg-sky-500' },
  at_risk:   { icon: ShieldAlert, color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   label: 'At Risk — Review required',  barColor: 'bg-amber-500' },
  unhealthy: { icon: ShieldX,     color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/20',     label: 'Unhealthy — Fix before publish', barColor: 'bg-red-500' },
};

const DIMENSION_META = {
  graph_integrity:      { icon: GitBranch, label: 'Graph Integrity' },
  terminal_coverage:    { icon: Target,    label: 'Terminal Coverage' },
  execution_readiness:  { icon: Zap,       label: 'Execution Readiness' },
  step_completeness:    { icon: FileText,  label: 'Step Completeness' },
  flow_coherence:       { icon: Workflow,  label: 'Flow Coherence' },
};

const SEVERITY_CONFIG = {
  critical: { icon: XCircle,       color: 'text-red-400',   bg: 'bg-red-500/10' },
  warning:  { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  info:     { icon: Info,          color: 'text-sky-400',   bg: 'bg-sky-500/10' },
};

function AnimatedScore({ value, color }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  
  useEffect(() => {
    const prev = prevRef.current;
    if (prev === value) return;
    prevRef.current = value;
    
    const diff = value - prev;
    const steps = Math.min(Math.abs(diff), 20);
    const increment = diff / steps;
    let current = prev;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      current += increment;
      setDisplay(Math.round(current));
      if (step >= steps) {
        setDisplay(value);
        clearInterval(timer);
      }
    }, 30);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return (
    <span data-testid="health-score-value" className={`text-lg font-bold tabular-nums ${color} transition-colors duration-500`}>
      {display}
    </span>
  );
}

export function FunnelHealthCard({ health, onIssueClick, onApplyFix, onApplyAllFixes }) {
  const [expanded, setExpanded] = useState(false);
  const [fixingAction, setFixingAction] = useState(null);
  const [recentlyFixed, setRecentlyFixed] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  
  if (!health) return null;
  
  const { health_score, health_status, dimensions, issues } = health;
  const cfg = STATUS_CONFIG[health_status] || STATUS_CONFIG.good;
  const StatusIcon = cfg.icon;
  
  const criticalCount = issues?.filter(i => i.severity === 'critical').length || 0;
  const warningCount = issues?.filter(i => i.severity === 'warning').length || 0;
  const fixableCount = issues?.filter(i => i.fix).length || 0;
  
  const handleFix = async (issue) => {
    if (!issue.fix || !onApplyFix || fixingAction || batchRunning) return;
    
    setFixingAction(issue.fix.action);
    setDiffData(null);
    setShowDiff(false);
    try {
      const result = await onApplyFix(issue.fix.action, issue.fix.payload || {});
      if (result?.applied) {
        setRecentlyFixed(issue.fix.action);
        // Store single fix as array for FixDiffPanel
        if (result.changes && result.changes.total_changes > 0) {
          setDiffData([{ applied: true, changes: result.changes, action: result.fix_type }]);
        }
        toast.success('Fix applied successfully', {
          description: issue.fix.label,
        });
        setTimeout(() => setRecentlyFixed(null), 3000);
      } else if (result?.error) {
        toast.info(result.error);
      }
    } catch (err) {
      toast.error('Failed to apply fix');
    } finally {
      setFixingAction(null);
    }
  };
  
  const handleFixAll = async () => {
    if (!onApplyAllFixes || batchRunning || fixingAction) return;
    
    setBatchRunning(true);
    setBatchResult(null);
    setDiffData(null);
    setShowDiff(false);
    try {
      const result = await onApplyAllFixes();
      if (result) {
        setBatchResult(result);
        // Store batch fixes for FixDiffPanel
        const appliedWithChanges = (result.applied_fixes || []).filter(f => f.applied && f.changes && f.changes.total_changes > 0);
        if (appliedWithChanges.length > 0) {
          setDiffData(appliedWithChanges);
        }
        const count = result.applied_fixes?.filter(f => f.applied).length || 0;
        if (count > 0) {
          toast.success(`${count} fix${count > 1 ? 'es' : ''} applied`, {
            description: `Score: ${result.initial_score} → ${result.final_score} (+${result.improvement})`,
          });
        } else {
          toast.info('No fixes could be applied');
        }
        // Clear batch result after 8 seconds (diff stays until manually closed)
        setTimeout(() => setBatchResult(null), 8000);
      }
    } catch (err) {
      toast.error('Batch fix failed');
    } finally {
      setBatchRunning(false);
    }
  };
  
  return (
    <Card data-testid="funnel-health-card" className={`border ${cfg.border} ${cfg.bg} transition-colors duration-500`}>
      <CardContent className="px-4 py-2.5">
        {/* Single row: Icon + Score + Status | Dimension bars | Controls */}
        <div className="flex items-center gap-4">
          {/* Left: Icon + Score + Status */}
          <div className="flex items-center gap-2.5 shrink-0">
            <div className={`p-1.5 rounded-lg ${cfg.bg} transition-colors duration-500`}>
              <StatusIcon className={`h-4 w-4 ${cfg.color} transition-colors duration-500`} />
            </div>
            <div className="flex items-baseline gap-1.5">
              <AnimatedScore value={health_score} color={cfg.color} />
              <span className="text-xs text-slate-500">/ 100</span>
            </div>
            <p data-testid="health-status-label" className="text-xs text-slate-400 hidden lg:block">{cfg.label}</p>
          </div>
          
          {/* Center: Dimension bars (inline) */}
          <div className="flex-1 grid grid-cols-5 gap-3 min-w-0">
            {Object.entries(dimensions || {}).map(([key, dim]) => {
              const meta = DIMENSION_META[key] || { icon: Shield, label: key };
              const DimIcon = meta.icon;
              const pct = dim.max > 0 ? (dim.score / dim.max) * 100 : 0;
              return (
                <div key={key} className="flex items-center gap-1.5" title={`${meta.label}: ${dim.score}/${dim.max}`}>
                  <DimIcon className="h-3 w-3 text-slate-500 shrink-0" />
                  <div className="flex-1 h-1 rounded-full bg-slate-700/50 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${pct >= 90 ? 'bg-emerald-500' : pct >= 70 ? 'bg-sky-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-slate-500 tabular-nums shrink-0">{dim.score}/{dim.max}</span>
                </div>
              );
            })}
          </div>
          
          {/* Right: Fix All + expand toggle */}
          <div className="flex items-center gap-2 shrink-0">
            {fixableCount > 0 && onApplyAllFixes && (
              <button
                data-testid="fix-all-btn"
                onClick={handleFixAll}
                disabled={batchRunning || !!fixingAction}
                className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-violet-500/20 text-violet-300 ring-1 ring-violet-500/30 hover:bg-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
              >
                {batchRunning ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Wrench className="h-3 w-3" />
                )}
                {batchRunning ? 'Fixing...' : `Fix All (${fixableCount})`}
              </button>
            )}
            {diffData && !showDiff && (
              <button
                data-testid="view-changes-btn"
                onClick={() => setShowDiff(true)}
                className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-slate-700/50 text-slate-300 ring-1 ring-slate-600/40 hover:bg-slate-700/70 hover:text-slate-200 transition-all flex items-center gap-1.5"
              >
                <Eye className="h-3 w-3" />
                View Changes
              </button>
            )}
            <button
              data-testid="health-expand-toggle"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          </div>
        </div>
        
        {/* Expanded: Dimension details + Issue list with fix buttons */}
        {expanded && (
          <div className="mt-4 space-y-3 border-t border-slate-700/50 pt-3">
            {/* Dimension breakdown */}
            <div className="space-y-2">
              {Object.entries(dimensions || {}).map(([key, dim]) => {
                const meta = DIMENSION_META[key] || { icon: Shield, label: key };
                const DimIcon = meta.icon;
                const pct = dim.max > 0 ? (dim.score / dim.max) * 100 : 0;
                return (
                  <div key={key} className="flex items-center gap-2">
                    <DimIcon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                    <span className="text-xs text-slate-300 w-32 truncate">{meta.label}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-slate-700/50 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${pct >= 90 ? 'bg-emerald-500' : pct >= 70 ? 'bg-sky-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-400 tabular-nums w-10 text-right">{dim.score}/{dim.max}</span>
                  </div>
                );
              })}
            </div>
            
            {/* Issues with fix buttons */}
            {issues && issues.length > 0 && (
              <div className="space-y-1.5">
                <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Issues</h4>
                {issues.map((issue, idx) => {
                  const sev = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.info;
                  const SevIcon = sev.icon;
                  const isFixing = fixingAction === issue.fix?.action;
                  const wasFixed = recentlyFixed === issue.fix?.action;
                  
                  return (
                    <div
                      key={idx}
                      data-testid={`health-issue-${idx}`}
                      className={`flex items-center justify-between px-2 py-1.5 rounded-md ${sev.bg} ${wasFixed ? 'animate-pulse bg-emerald-500/20' : ''}`}
                    >
                      <button
                        onClick={() => onIssueClick?.(issue)}
                        className="flex items-start gap-2 text-left min-w-0 flex-1"
                      >
                        <SevIcon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${sev.color}`} />
                        <div className="min-w-0">
                          <span className={`text-xs ${sev.color}`}>{issue.message}</span>
                          {issue.node_id && (
                            <span className="text-[10px] text-slate-500 ml-1">({issue.node_id})</span>
                          )}
                        </div>
                      </button>
                      
                      {/* Fix button */}
                      {issue.fix && onApplyFix && (
                        <button
                          data-testid={`fix-btn-${issue.fix.action}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFix(issue);
                          }}
                          disabled={isFixing || !!fixingAction}
                          className="shrink-0 ml-2 px-2 py-1 rounded text-[10px] font-medium bg-violet-500/20 text-violet-300 ring-1 ring-violet-500/30 hover:bg-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                        >
                          {isFixing ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : wasFixed ? (
                            <Check className="h-3 w-3 text-emerald-400" />
                          ) : (
                            <Wrench className="h-3 w-3" />
                          )}
                          {issue.fix.label}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {(!issues || issues.length === 0) && (
              <p className="text-xs text-emerald-400/80 text-center py-2">No issues found</p>
            )}
            
            {/* Batch fix result breakdown */}
            {batchResult && batchResult.applied_fixes?.length > 0 && (
              <div data-testid="batch-fix-result" className="space-y-1.5 border-t border-slate-700/50 pt-3">
                <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Check className="h-3 w-3 text-emerald-400" />
                  Batch Fix Results
                  <span className="text-emerald-400 font-bold ml-auto tabular-nums">
                    +{batchResult.improvement} pts
                  </span>
                </h4>
                {batchResult.applied_fixes.map((fix, idx) => (
                  <div
                    key={idx}
                    data-testid={`batch-fix-item-${idx}`}
                    className={`flex items-center justify-between px-2 py-1.5 rounded-md text-xs ${fix.applied ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {fix.applied ? (
                        <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                      ) : (
                        <XCircle className="h-3 w-3 text-red-400 shrink-0" />
                      )}
                      <span className="truncate">{fix.action.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 tabular-nums text-[10px]">
                      <span className="text-slate-500">{fix.score_before}</span>
                      <span className="text-slate-600">&rarr;</span>
                      <span className={fix.improvement > 0 ? 'text-emerald-400' : 'text-slate-400'}>{fix.score_after}</span>
                      {fix.improvement > 0 && (
                        <span className="text-emerald-400 font-medium">+{fix.improvement}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* MA-FUNNEL-DIFF-PANEL-016: View Changes panel */}
        {showDiff && diffData && (
          <div className="mt-3">
            <FixDiffPanel
              fixResults={diffData}
              onClose={() => { setShowDiff(false); setDiffData(null); }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FunnelHealthCard;
